import { CombinedState, combineReducers, Reducer } from 'redux';
import { reducer as formReducer } from 'redux-form';
import { createBrowserHistory as createHistory } from 'history';
import { connectRouter } from 'connected-react-router';

import asyncDataFetchReducer from '@bufferapp/async-data-fetch/lib/reducer';
import notificationsReducer from '@bufferapp/notifications/lib/reducer';

import queueReducer from '~/queue/reducer';
import composerPopoverReducer from '~/composer-popover/reducer';
import sentReducer from '~/sent/reducer';
import gridReducer from '~/grid/reducer';
import storiesReducer from '~/stories/reducer';
import pastRemindersReducer from '~/past-reminders/reducer';
import pendingTiktokReducer from '~/pending-tiktok/reducer';
import draftsReducer from '~/drafts/reducer';
import postingScheduleReducer from '~/posting-schedule/reducer';
import generalSettingsReducer from '~/general-settings/reducer';
import languageSettingsReducer from '~/language-preferences/reducer';
import i18nReducer from '~/i18n/reducer';
import profileSidebarReducer from '~/profile-sidebar/reducer';
import clientAccessReducer from '~/client-access/reducer';
import switchPlanModalReducer from '~/switch-plan-modal/reducer';
import stripeReducer from '~/stripe/reducer';
import modalsReducer from '~/modals/reducer';
import manageAppsReducer from '~/manage-apps-extras/reducer';
import twoFactorAuthReducer from '~/two-factor-auth/reducer';
import profilesDisconnectedModalReducer from '~/profiles-disconnected-modal/reducer';
import accountNotificationsReducer from '~/account-notifications/reducer';
import trialCompleteModalReducer from '~/trial-complete-modal/reducer';
import campaignFormReducer from '~/campaign-form/reducer';
import campaignReducer from '~/campaign/reducer';
import campaignsListReducer from '~/campaigns-list/reducer';
import deleteCampaignModalReducer from '~/delete-campaign-modal/reducer';
import hashtagGroupsReducer from '~/hashtag-group-manager/reducer';
import globalAccountReducer from '~/globalAccount/reducer';
import closeComposerModalReducer from '~/close-composer-confirmation-modal/reducer';
import storyPreviewReducer from '~/story-preview/reducer';
import storyGroupComposerReducer from '~/story-group-composer/reducer';
import temporaryBannerReducer from '~/temporary-banner/reducer';
import creditCardFormReducer from '~/credit-card-form/reducer';
import userReducer from '~/user/reducer';
import orgReducer from '~/organizations/reducer';
import profileNavReducer from '~/profile-nav/reducer';
import { postsSlice } from '~/post/slice';
import { aiLimitsSlice } from '~/ai/state/aiLimits/slice';
import { contentIdeationSlice } from '~/ai/state/contentIdeation/slice';
import stealProfileModalReducer from '~/steal-profile-modal/reducer';
import thirdpartyReducer from '~/thirdParty/reducer';
import { uploadsSlice } from '~/uploads/state/slice';
import { notificationsSlice } from '~/notifications-provider/notificationsSlice';
import { ideasSlice } from '~/content/state/ideasSlice';
import { integrationBarSlice } from '~/integrations-bar/state/integrationBarSlice';
import { contentGenerationSlice } from '~/ai/state/contentGeneration/slice';
import calendarReducer from '../calendar/reducer';
import welcomeChecklistReducer from '../welcome-checklist/reducer';

import { environmentSlice } from './slices/environmentSlice';

export const history = createHistory();

const reducers = {
  router: connectRouter(history),
  user: userReducer,
  organizations: orgReducer,
  profileNav: profileNavReducer,
  form: formReducer,
  queue: queueReducer,
  composerPopover: composerPopoverReducer,
  sent: sentReducer,
  grid: gridReducer,
  stories: storiesReducer,
  pastReminders: pastRemindersReducer,
  pendingTiktok: pendingTiktokReducer,
  i18n: i18nReducer,
  profileSidebar: profileSidebarReducer,
  clientAccess: clientAccessReducer,
  asyncDataFetch: asyncDataFetchReducer,
  notifications: notificationsReducer,
  switchPlanModal: switchPlanModalReducer,
  stripe: stripeReducer,
  modals: modalsReducer,
  manageAppsExtras: manageAppsReducer,
  twoFactorAuth: twoFactorAuthReducer,
  drafts: draftsReducer,
  generalSettings: generalSettingsReducer,
  languageSettings: languageSettingsReducer,
  postingSchedule: postingScheduleReducer,
  profilesDisconnectedModal: profilesDisconnectedModalReducer,
  accountNotifications: accountNotificationsReducer,
  stealProfileModal: stealProfileModalReducer,
  thirdparty: thirdpartyReducer,
  trialCompleteModal: trialCompleteModalReducer,
  campaignForm: campaignFormReducer,
  campaign: campaignReducer,
  campaignsList: campaignsListReducer,
  deleteCampaignModal: deleteCampaignModalReducer,
  hashtagGroups: hashtagGroupsReducer,
  globalAccount: globalAccountReducer,
  closeComposerModal: closeComposerModalReducer,
  storyPreview: storyPreviewReducer,
  storyGroupComposer: storyGroupComposerReducer,
  temporaryBanner: temporaryBannerReducer,
  creditCardForm: creditCardFormReducer,
  calendar: calendarReducer,
  welcomeChecklist: welcomeChecklistReducer,
  // this manual wiring could be simplified with Redux Toolkit configureStore
  [notificationsSlice.name]: notificationsSlice.reducer,
  [environmentSlice.name]: environmentSlice.reducer,
  [postsSlice.name]: postsSlice.reducer,
  [uploadsSlice.name]: uploadsSlice.reducer,
  [ideasSlice.name]: ideasSlice.reducer,
  [aiLimitsSlice.name]: aiLimitsSlice.reducer,
  [contentIdeationSlice.name]: contentIdeationSlice.reducer,
  [integrationBarSlice.name]: integrationBarSlice.reducer,
  [contentGenerationSlice.name]: contentGenerationSlice.reducer,
};

export const rootReducer = combineReducers(reducers);
export type RootState = ReturnType<typeof rootReducer>;

// This method combines all our reducers together
// to allow asynchronously adding more reducers
export const createReducer = (
  asyncReducers = {}
): Reducer<CombinedState<RootState>> =>
  combineReducers({
    ...reducers,
    ...asyncReducers,
  });
